import type {
  FieldDefinition,
  ValidationRule,
  VisibilityCondition,
  FormConfiguration,
  SpaceDetails,
  StepCompletionMap,
  StepCompletionResult,
} from '@store/models/booking';
import { parseStoredDateToLocalDate } from './timeZone';

export interface StepValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validates all visible fields in a step against their validation rules.
 */
export function validateStep(
  fields: FieldDefinition[],
  formData: Record<string, unknown>,
  t: (key: string, options?: Record<string, unknown>) => string
): StepValidationResult {
  const errors: Record<string, string> = {};
  const visibleFields = getVisibleFields(fields, formData);

  for (const field of visibleFields) {
    if (!field.validation) continue;
    const value = formData[field.key] ?? field.defaultValue;
    for (const rule of field.validation) {
      const error = validateRule(rule, value, t);
      if (error) {
        errors[field.key] = error;
        break;
      }
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateRule(
  rule: ValidationRule,
  value: unknown,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  switch (rule.type) {
    case 'required':
      return isEmptyValue(value) ? t(rule.messageKey) : null;

    case 'minLength': {
      if (typeof value !== 'string') return null;
      const min = rule.value as number;
      return value.length < min ? t(rule.messageKey, { min }) : null;
    }

    case 'maxLength': {
      if (typeof value !== 'string') return null;
      const max = rule.value as number;
      return value.length > max ? t(rule.messageKey, { max }) : null;
    }

    case 'min': {
      if (typeof value !== 'number') return null;
      const min = rule.value as number;
      return value < min ? t(rule.messageKey, { min }) : null;
    }

    case 'max': {
      if (typeof value !== 'number') return null;
      const max = rule.value as number;
      return value > max ? t(rule.messageKey, { max }) : null;
    }

    case 'pattern': {
      if (typeof value !== 'string') return null;
      try {
        return new RegExp(rule.value as string).test(value) ? null : t(rule.messageKey);
      } catch {
        console.warn(`Invalid regex pattern: ${rule.value}`);
        return null;
      }
    }

    case 'futureDate': {
      if (!value || typeof value !== 'object') return null;
      const range = value as { start?: string; end?: string };
      if (!range.start) return null;
      // Parse the stored YYYY-MM-DD as a LOCAL calendar date (local midnight),
      // not as UTC. `new Date('2026-07-20')` would be parsed as UTC midnight,
      // which is earlier than local midnight in negative-UTC-offset (Americas)
      // timezones and would wrongly reject same-day (walk-in) bookings.
      const startDate = parseStoredDateToLocalDate(range.start);
      if (!startDate) return null;
      startDate.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return startDate < now ? t(rule.messageKey) : null;
    }

    case 'dateRangeComplete': {
      if (!value || typeof value !== 'object') return t(rule.messageKey);
      const drc = value as { start?: string; end?: string };
      if (!drc.start || !drc.end) return t(rule.messageKey);
      return null;
    }

    case 'dateRangeOrder': {
      if (!value || typeof value !== 'object') return null;
      const dr = value as { start?: string; end?: string };
      if (!dr.start || !dr.end) return null;
      return new Date(dr.end) < new Date(dr.start) ? t(rule.messageKey) : null;
    }

    case 'maxBookingWindow': {
      if (!value || typeof value !== 'object') return null;
      const range = value as { start?: string; end?: string };
      if (!range.end) return null;
      const years = typeof rule.value === 'number' ? rule.value : 1;
      const end = new Date(range.end);
      if (Number.isNaN(end.getTime())) return null;
      const today = new Date();
      // Compare calendar dates only: end uses UTC components (so YYYY-MM-DD parses
      // as the picked day), today uses local components (the day shown in the UI).
      const endNum = end.getUTCFullYear() * 10000 + end.getUTCMonth() * 100 + end.getUTCDate();
      const limitNum =
        (today.getFullYear() + years) * 10000 + today.getMonth() * 100 + today.getDate();
      return endNum > limitNum ? t(rule.messageKey, { years }) : null;
    }

    case 'maxEventDuration': {
      if (!value || typeof value !== 'object') return null;
      const range = value as { start?: string; end?: string };
      if (!range.start || !range.end) return null;
      const months = typeof rule.value === 'number' ? rule.value : 1;
      const start = new Date(range.start);
      const end = new Date(range.end);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
      const limit = new Date(
        Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, start.getUTCDate())
      );
      const endNum = end.getUTCFullYear() * 10000 + end.getUTCMonth() * 100 + end.getUTCDate();
      const limitNum =
        limit.getUTCFullYear() * 10000 + limit.getUTCMonth() * 100 + limit.getUTCDate();
      return endNum > limitNum ? t(rule.messageKey, { months }) : null;
    }

    case 'timeRangeComplete': {
      if (!value || typeof value !== 'object') return t(rule.messageKey);
      const trc = value as { start?: string; end?: string };
      if (!trc.start || !trc.end) return t(rule.messageKey);
      return null;
    }

    case 'timeRangeOrder': {
      if (!value || typeof value !== 'object') return null;
      const tr = value as { start?: string; end?: string };
      if (!tr.start || !tr.end) return null;
      return tr.end <= tr.start ? t(rule.messageKey) : null;
    }

    case 'email': {
      if (typeof value !== 'string' || value.trim() === '') return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value) ? null : t(rule.messageKey);
    }

    case 'contactList': {
      if (!Array.isArray(value)) return null;
      type Contact = { name?: string; email?: string };
      const contacts = value as Contact[];
      const nameOf = (c: Contact) => (c.name ?? '').trim();
      const emailOf = (c: Contact) => (c.email ?? '').trim();
      const hasValidContact = contacts.some((c) => nameOf(c) !== '' && emailOf(c) !== '');
      if (!hasValidContact) return t(rule.messageKey);
      // Every rendered row must have both a name and an email.
      const hasIncompleteRow = contacts.some((c) => nameOf(c) === '' || emailOf(c) === '');
      if (hasIncompleteRow) return t(rule.messageKey);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasInvalidEmail = contacts.some(
        (c) => emailOf(c) !== '' && !emailRegex.test(emailOf(c))
      );
      return hasInvalidEmail ? t(rule.messageKey, { reason: 'email' }) : null;
    }

    /* c8 ignore start */
    default: {
      const _exhaustiveType: never = rule.type;
      console.warn(`Unrecognized validation type: ${_exhaustiveType as string}`);
      return null;
    }
  }
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return !value;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Evaluates whether a field should be visible based on its visibility condition.
 */
export function evaluateVisibility(
  condition: VisibilityCondition,
  formData: Record<string, unknown>
): boolean {
  const { fieldKey, operator, value: expected } = condition;

  if (!(fieldKey in formData)) {
    console.warn(`visibleWhen references non-existent field: ${fieldKey}`);
    return false;
  }

  const actual = formData[fieldKey];

  switch (operator) {
    case 'equals':
      return actual === expected;
    case 'notEquals':
      return actual !== expected;
    case 'contains':
      if (typeof actual === 'string' && typeof expected === 'string')
        return actual.includes(expected);
      if (Array.isArray(actual)) return actual.includes(expected);
      return false;
    case 'notEmpty':
      return !isEmptyValue(actual);
    /* c8 ignore start */
    default: {
      const _exhaustiveOp: never = operator;
      console.warn(`Unrecognized visibility operator: ${_exhaustiveOp as string}`);
      return false;
    }
  }
}

/**
 * Returns only the visible fields from a list, filtering by visibility conditions.
 */
export function getVisibleFields(
  fields: FieldDefinition[],
  formData: Record<string, unknown>
): FieldDefinition[] {
  return fields.filter((field) => {
    if (!field.visibleWhen) return true;
    return evaluateVisibility(field.visibleWhen, formData);
  });
}

export function validateAllSteps(
  config: FormConfiguration,
  formData: Record<string, unknown>,
  t: (key: string, options?: Record<string, unknown>) => string
): StepCompletionMap {
  const stepResults: Record<number, StepCompletionResult> = {};
  const incompleteStepNames: string[] = [];
  const reviewStepIndex = config.steps.findIndex((s) => s.type === 'review');
  const maxStepIndex = reviewStepIndex >= 0 ? reviewStepIndex : config.steps.length;

  for (let i = 0; i < maxStepIndex; i++) {
    const step = config.steps[i];
    if (!step) continue;
    const stepName = t(step.titleKey);

    if (step.type === 'space-selection') {
      const space = formData.selectedSpace as SpaceDetails | undefined;
      const isComplete = !!space?.id;
      stepResults[i] = { isComplete, stepIndex: i, stepName };
      if (!isComplete) incompleteStepNames.push(stepName);
      continue;
    }

    if (step.type === 'form') {
      const fields: FieldDefinition[] = [
        ...(step.fields ?? []),
        ...(step.sections?.flatMap((s) => s.fields) ?? []),
      ].filter((f) => f.key !== 'policyAcknowledgement');
      const result = validateStep(fields, formData, t);
      stepResults[i] = { isComplete: result.isValid, stepIndex: i, stepName };
      if (!result.isValid) incompleteStepNames.push(stepName);
      continue;
    }

    stepResults[i] = { isComplete: true, stepIndex: i, stepName };
  }

  const allComplete = Object.values(stepResults).every((r) => r.isComplete);
  return { stepResults, allComplete, incompleteStepNames };
}
